/**
 * useTakeInCarico — F-007 Phase 4 (IMPL-P4-004).
 *
 * Mutation per prendere in carico una pratica (`POST /:id/take`).
 *
 * Race-condition handling (cfr. INTRODUCTION.md §"Architectural Decisions" →
 * `sonner`):
 *   - Su 409 (`PRATICA_ALREADY_ASSIGNED`) il backend risponde con
 *     `{ assignedOperatorName }`; il client mostra `toast.error(...)` con il
 *     nome dell'operatore che ha vinto la race e re-invalida la queue per
 *     allineare la lista locale.
 *
 * Su success invalida sia `['operator', 'queue']` sia `['operator', 'my-assigned']`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { operatorClient, type OperatorApiError } from '../lib/operator-client';

interface TakeErrorBody {
  assignedOperatorName?: string;
  code?: string;
}

export function useTakeInCarico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (praticaId: string) => operatorClient.take(praticaId),
    onError: (err: Error) => {
      const apiErr = err as OperatorApiError;
      if (apiErr.status === 409) {
        const body = (apiErr.body ?? {}) as TakeErrorBody;
        const name = body.assignedOperatorName ?? 'un altro operatore';
        toast.error(`Pratica già presa in carico da ${name}`);
        qc.invalidateQueries({ queryKey: ['operator', 'queue'] });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'queue'] });
      qc.invalidateQueries({ queryKey: ['operator', 'my-assigned'] });
    },
  });
}
